/**
 * Utilities for rendering HTML file previews via iframe `srcDoc`.
 *
 * `prepareSrcDocHtml` injects force-light declarations (and optional island
 * edit styles + contenteditable) before the document is loaded. On first load
 * callers set the returned string as `srcDoc`. On subsequent content changes,
 * use `morphPreparedHtmlIntoDocument` instead of replacing `srcDoc`.
 * `computeHtmlSrcDocFingerprint` gates when an update is needed.
 */

const FNV_OFFSET_BASIS_32 = 0x811c9dc5;
const FNV_PRIME_32 = 16777619;

function fnv1a32(input: string): string {
  let hash = FNV_OFFSET_BASIS_32;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME_32) >>> 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * CSS injected into the iframe to highlight editable islands.
 * Uses `outline` (not border) so it doesn't shift layout.
 */
const ISLAND_EDIT_CSS = `
  [data-edit-id]:not([data-no-edit]) {
    outline: 2px solid transparent;
    outline-offset: 2px;
    cursor: text;
    transition: outline-color 0.15s, background-color 0.15s;
  }
  [data-edit-id]:not([data-no-edit]):hover {
    outline-color: rgba(99, 102, 241, 0.35);
  }
  [data-edit-id]:not([data-no-edit]):focus {
    outline-color: rgb(99, 102, 241);
  }
  [data-edit-id]:not([data-no-edit]) a,
  [data-edit-id]:not([data-no-edit]) button {
    cursor: text;
  }
`;

/**
 * Runs inside the iframe on load when `editable` is true. Blocks navigation
 * and control activation inside editable islands while still allowing the
 * contenteditable caret to be placed in link/button text.
 */
const EDITABLE_NAVIGATION_GUARD_SCRIPT = `
(function () {
  function editableIsland(node) {
    return node && node.closest && node.closest('[data-edit-id]:not([data-no-edit])');
  }

  // Prevent buttons and form controls from stealing focus on mousedown.
  // Links are intentionally excluded — blocking mousedown there prevents
  // placing the caret inside link text; navigation is blocked on click.
  document.addEventListener('mousedown', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!editableIsland(t)) return;
    if (t.closest('button, input, select, textarea')) {
      e.preventDefault();
    }
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (editableIsland(t)) {
      if (t.closest('a, button, input, select, textarea')) {
        e.preventDefault();
      }
      return;
    }

    var a = t.closest('a');
    if (!a) return;
    e.preventDefault();
    var href = a.getAttribute('href');
    if (!href || href === '#') return;
    if (href.charAt(0) === '#') {
      var target = document.getElementById(href.slice(1));
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  }, true);

  document.querySelectorAll('form').forEach(function (form) {
    form.addEventListener('submit', function (e) { e.preventDefault(); });
  });
})();
`;

const FORCE_LIGHT_HEAD =
  '<meta name="color-scheme" content="light" data-cohesive-injected>' +
  "<style data-cohesive-force-light data-cohesive-injected>:root{color-scheme:light !important;}</style>";

export interface PrepareSrcDocHtmlOptions {
  /** When true, inject island edit CSS and set contenteditable on islands. */
  editable?: boolean;
}

/**
 * Parse `html`, inject force-light head content (and optional island edit
 * styles + contenteditable), and serialize for iframe `srcDoc`.
 */
export function prepareSrcDocHtml(
  html: string,
  options: PrepareSrcDocHtmlOptions = {},
): string {
  const { editable = false } = options;
  const doc = new DOMParser().parseFromString(html, "text/html");

  let head = doc.head;
  if (!head) {
    head = doc.createElement("head");
    doc.documentElement.insertBefore(head, doc.body);
  }

  const headFragment = doc.createElement("template");
  headFragment.innerHTML = FORCE_LIGHT_HEAD;
  if (editable) {
    const style = doc.createElement("style");
    style.setAttribute("data-cohesive-edit", "");
    style.setAttribute("data-cohesive-injected", "");
    style.textContent = ISLAND_EDIT_CSS;
    headFragment.content.appendChild(style);
  }
  head.appendChild(headFragment.content);

  if (editable) {
    doc
      .querySelectorAll<HTMLElement>("[data-edit-id]:not([data-no-edit])")
      .forEach((el) => {
        el.setAttribute("contenteditable", "true");
        el.setAttribute("spellcheck", "false");

        const editId = el.getAttribute("data-edit-id");
        if (editId && !el.id) {
          el.id = editId;
        }

        el.querySelectorAll("button").forEach((button) => {
          button.setAttribute("tabindex", "-1");
          if (!button.getAttribute("type")) {
            button.setAttribute("type", "button");
          }
        });
      });

    const navGuard = doc.createElement("script");
    navGuard.setAttribute("data-cohesive-nav-guard", "");
    navGuard.setAttribute("data-cohesive-injected", "");
    navGuard.setAttribute("im-preserve", "true");
    navGuard.textContent = EDITABLE_NAVIGATION_GUARD_SCRIPT;
    head.appendChild(navGuard);
  }

  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
}

/**
 * Stable fingerprint for iframe remounting. Excludes presigned URL tokens so
 * URL rotation does not reload the iframe. `contentHash` is preferred when
 * available (server-side content identity); falls back to FNV-1a of `body`.
 */
export function computeHtmlSrcDocFingerprint(
  body: string,
  contentHash: string | undefined,
  missingPresignIds?: Iterable<string>,
): string {
  const bodyKey =
    contentHash && contentHash.length > 0 ? contentHash : fnv1a32(body);
  const missingKey = missingPresignIds
    ? [...missingPresignIds].sort().join(",")
    : "";
  return `${bodyKey}:${missingKey}`;
}
